/**
 * 現場の 3D に描くものを、描画ライブラリに依存せずに作る（CSSP-SPEC-001 §7.2）。
 * サンプル（WebUI-Sample/3D-Portal-Google3D.html）の trs・sampleH・buildCtx・updateTerrain・
 * buildSiteScene・computeEquip・dynItems と同じ式。座標は現場のローカル（x=東, y=上, z=南、
 * 原点は施工範囲の中心、高さ 0 は T.P. yRef）。
 */
import { clamp, lerp } from '../core/math';
import { sampleContext } from '../model';
import type { Site, SiteState } from '../model';

export type Rgb = [number, number, number];
export type Rgba = [number, number, number, number];
/** 列優先 4×4 行列（y 軸回りの回転・拡大・平行移動）。単位箱は底面中心が原点、高さ 1。 */
export type Mat = Float32Array;
export interface Item {
  m: Mat;
  c: Rgba;
}

export const hex = (h: string): Rgb => {
  const n = parseInt(h.slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
};
export const mix = (a: readonly number[], b: readonly number[], t: number): Rgb => [
  lerp(a[0]!, b[0]!, t),
  lerp(a[1]!, b[1]!, t),
  lerp(a[2]!, b[2]!, t),
];
export const rgba = (c: readonly number[], a = 1): Rgba => [c[0]!, c[1]!, c[2]!, a];

/** サンプルの trs。大きさが 0 に近いと行列が退化するので ±0.001 に寄せる。 */
export function trs(
  x: number,
  y: number,
  z: number,
  ry: number,
  sx: number,
  sy: number,
  sz: number,
): Mat {
  const c = Math.cos(ry);
  const s = Math.sin(ry);
  const f = (v: number) => (Math.abs(v) < 1e-3 ? (v < 0 ? -1e-3 : 1e-3) : v);
  sx = f(sx);
  sy = f(sy);
  sz = f(sz);
  return new Float32Array([c * sx, 0, -s * sx, 0, 0, sy, 0, 0, s * sz, 0, c * sz, 0, x, y, z, 1]);
}

/** 現場ごとの描画用の状態（表示日の地面 H と色、地形の付加情報）。Site は書きかえない。 */
export interface SiteDraw {
  site: Site;
  /** 西→東の進み具合の座標（0〜1）と、草地の色むら。 */
  R: Float32Array;
  VAR: Float32Array;
  /** 表示日の地面（yRef からの相対）。 */
  H: Float32Array;
  col: Float32Array;
  /** 等高線の間隔（m）。 */
  contI: number;
}

export function createSiteDraw(site: Site): SiteDraw {
  const g = site.g;
  const R = new Float32Array(g.nv);
  const VAR = new Float32Array(g.nv);
  for (let j = 0; j <= g.nz; j++) {
    for (let i = 0; i <= g.nx; i++) {
      const k = j * (g.nx + 1) + i;
      R[k] = i / g.nx;
      VAR[k] = 0.5 + 0.5 * Math.sin(i * 12.9898 + j * 78.233) * Math.sin(i * 3.1 + j * 1.7);
    }
  }
  const rng = Math.max(1, site.ctx.cmax - site.ctx.cmin);
  return {
    site,
    R,
    VAR,
    H: new Float32Array(g.E),
    col: new Float32Array(g.nv * 3),
    contI: rng < 12 ? 1 : rng < 50 ? 2 : 5,
  };
}

/** 現場の地面の高さ（施工範囲の中は表示日の地面、外は周辺 1km の標高）。サンプルの sampleH。 */
export function sampleH(d: SiteDraw, lx: number, lz: number): number {
  const s = d.site;
  const g = s.g;
  const fx = (lx + s.area.w / 2) / g.dx;
  const fz = (lz + s.area.d / 2) / g.dz;
  if (fx < 0 || fz < 0 || fx > g.nx || fz > g.nz) return sampleContext(s.ctx, lx, lz) - s.yRef;
  const i = Math.min(Math.floor(fx), g.nx - 1);
  const j = Math.min(Math.floor(fz), g.nz - 1);
  const u = fx - i;
  const v = fz - j;
  const W = g.nx + 1;
  const H = d.H;
  return lerp(
    lerp(H[j * W + i]!, H[j * W + i + 1]!, u),
    lerp(H[(j + 1) * W + i]!, H[(j + 1) * W + i + 1]!, u),
    v,
  );
}

/** 周辺 1km の地形（簡易地形モード）。施工範囲の内側は沈めて重ならないようにする。 */
export function contextMesh(d: SiteDraw): {
  n: number;
  X: Float32Array;
  Z: Float32Array;
  H: Float32Array;
  COL: Float32Array;
  skirtBase: number;
} {
  const s = d.site;
  const c = s.ctx;
  const cn = c.cn;
  const W = cn + 1;
  const st = c.cw / cn;
  const nv = W * W;
  const X = new Float32Array(nv);
  const Z = new Float32Array(nv);
  const H = new Float32Array(nv);
  const COL = new Float32Array(nv * 3);
  const rng = Math.max(1, c.cmax - c.cmin);
  const hw = s.area.w / 2 - 1;
  const hd = s.area.d / 2 - 1;
  const low = s.g.minH - 3;
  const a = hex('#b9c99a');
  const b = hex('#c9b48a');
  for (let j = 0; j <= cn; j++) {
    for (let i = 0; i <= cn; i++) {
      const k = j * W + i;
      const x = -c.cw / 2 + i * st;
      const z = -c.cw / 2 + j * st;
      X[k] = x;
      Z[k] = z;
      H[k] = Math.abs(x) < hw && Math.abs(z) < hd ? low : c.C[k]! - s.yRef;
      COL.set(mix(a, b, ((c.C[k]! - c.cmin) / rng) * 0.85), k * 3);
    }
  }
  return { n: cn, X, Z, H, COL, skirtBase: c.cmin - s.yRef - 12 };
}

const GRASS1 = hex('#a3b77f');
const GRASS2 = hex('#93a86f');
const SOIL = hex('#b98d55');
const FIN = hex('#d6c8a8');
const GREEN = hex('#7fa65a');
const WHITE: Rgb = [0.97, 0.98, 0.99];
const RED = hex('#dc2626');
const BLUE = hex('#2563eb');

/** 表示日の地面の高さと色を作り直す（サンプルの updateTerrain）。 */
export function updateGround(d: SiteDraw, st: SiteState, heat: boolean): void {
  const g = d.site.g;
  const e = st.e;
  const band = 0.12;
  const green = st.roles.green ?? 0;
  for (let k = 0; k < g.nv; k++) {
    const vp = clamp((e * (1 + band) - d.R[k]!) / band, 0, 1);
    const diff = g.E[k]! - g.D[k]!;
    d.H[k] = g.E[k]! + (g.D[k]! - g.E[k]!) * vp;
    let c: Rgb;
    if (heat) {
      const t = clamp(diff / 4, -1, 1);
      c = t > 0 ? mix(WHITE, RED, t) : mix(WHITE, BLUE, -t);
    } else if (Math.abs(diff) < 0.15 || vp <= 0) c = mix(GRASS1, GRASS2, d.VAR[k]!);
    else if (vp < 1) c = mix(SOIL, FIN, vp * 0.3);
    else c = g.SL[k]! > 0.22 ? mix(FIN, GREEN, green) : FIN;
    d.col[k * 3] = c[0];
    d.col[k * 3 + 1] = c[1];
    d.col[k * 3 + 2] = c[2];
  }
}

/** 計画面の網目（約 6m 間隔）の線。点はローカル座標 [x, y, z]。 */
export function designLines(site: Site): [number, number, number][][] {
  const g = site.g;
  const W = g.nx + 1;
  const step = Math.max(1, Math.round(6 / g.dx));
  const lines: [number, number, number][][] = [];
  for (let j = 0; j <= g.nz; j += step) {
    const pts: [number, number, number][] = [];
    for (let i = 0; i <= g.nx; i++) {
      const k = j * W + i;
      pts.push([g.LX[k]!, g.D[k]! + 0.15, g.LZ[k]!]);
    }
    lines.push(pts);
  }
  for (let i = 0; i <= g.nx; i += step) {
    const pts: [number, number, number][] = [];
    for (let j = 0; j <= g.nz; j++) {
      const k = j * W + i;
      pts.push([g.LX[k]!, g.D[k]! + 0.15, g.LZ[k]!]);
    }
    lines.push(pts);
  }
  return lines;
}

/** 構造物の下端（サンプルの b.y0f）。 */
export function blockBase(site: Site, b: Site['blocks'][number]): number {
  const F = site.FH - site.yRef;
  if (b.kind === 'bldg') return b.basement ? F - site.pit * (b.pitK ?? 1) + 0.8 : F;
  if (b.kind === 'seg') return F + b.y0;
  if (b.kind === 'water') return F - site.pit + 0.3;
  return F;
}

/** 一番高いところ（ラベルと視点の高さ）。サンプルの siteTop。 */
export function siteTop(site: Site): number {
  const F = site.FH - site.yRef;
  return Math.max(
    site.g.maxH,
    ...site.blocks.map((b) =>
      b.kind === 'bldg'
        ? blockBase(site, b) + b.n * b.uh
        : blockBase(site, b) + ('h' in b ? b.h : 1),
    ),
    site.crane ? F + site.crane.h : 0,
  );
}

export interface SceneItems {
  solids: Item[];
  edges: Item[];
  ghosts: Item[];
  gedges: Item[];
  trans: Item[];
}

const ORANGE = hex('#f59e0b');
const CONC = hex('#b8bcc2');

/** 構造物・仮囲い・ゲート（サンプルの buildSiteScene）。 */
export function sceneItems(d: SiteDraw, st: SiteState): SceneItems {
  const s = d.site;
  const S: SceneItems = { solids: [], edges: [], ghosts: [], gedges: [], trans: [] };
  const acc = hex(s.color);
  const box = (lx: number, y: number, lz: number, w: number, h: number, dd: number) =>
    trs(lx, y, lz, 0, w, h, dd);
  const ec = (c: Rgb, a: number): Rgba => [c[0] * 0.42, c[1] * 0.42, c[2] * 0.42, a];
  const solid = (m: Mat, c: Rgb, a = 0.5) => {
    S.solids.push({ m, c: rgba(c) });
    S.edges.push({ m, c: ec(c, a) });
  };
  const fence = (x0: number, z0: number, x1: number, z1: number) => {
    const n = Math.max(2, Math.round(Math.hypot(x1 - x0, z1 - z0) / 10));
    for (let i = 0; i < n; i++) {
      const xa = lerp(x0, x1, i / n);
      const za = lerp(z0, z1, i / n);
      const xb = lerp(x0, x1, (i + 1) / n);
      const zb = lerp(z0, z1, (i + 1) / n);
      const y = (sampleH(d, xa, za) + sampleH(d, xb, zb)) / 2;
      S.solids.push({
        m: trs(
          (xa + xb) / 2,
          y,
          (za + zb) / 2,
          Math.atan2(-(zb - za), xb - xa),
          Math.hypot(xb - xa, zb - za),
          2.2,
          0.25,
        ),
        c: [0.96, 0.62, 0.04, 1],
      });
    }
  };
  const hw = s.area.w / 2;
  const hd = s.area.d / 2;
  fence(-hw, -hd, hw, -hd);
  fence(hw, -hd, hw, hd);
  fence(hw, hd, -hw, hd);
  fence(-hw, hd, -hw, -hd);
  const gt = s.gate;
  const gy = sampleH(d, gt.x, gt.z);
  for (const k of [-1, 1]) solid(box(gt.x + 5 * k, gy, gt.z, 0.6, 4, 0.6), hex('#475569'));
  solid(box(gt.x, gy + 3.6, gt.z, 10.6, 0.5, 0.6), ORANGE);
  const role = (r: string) => (st.roles as Record<string, number | undefined>)[r] ?? 0;
  for (const b of s.blocks) {
    if (b.kind === 'bldg') {
      const fr = role('frame');
      const fi = role('finish');
      const fo = role('found');
      const y0 = blockBase(s, b);
      const done = fr * b.n;
      const full = Math.floor(done + 1e-6);
      const frac = done - full;
      const fin = Math.min(full, Math.floor(fi * b.n + 1e-6));
      const light = mix(acc, [1, 1, 1], 0.5);
      if (fo > 0)
        solid(
          box(b.x, y0 - 0.8, b.z, b.w + 2, 0.8 * clamp(fo, 0.15, 1), b.d + 2),
          hex('#9ca3af'),
          0.3,
        );
      for (let i = 0; i < full; i++)
        solid(box(b.x, y0 + i * b.uh, b.z, b.w, b.uh * 0.94, b.d), i < fin ? light : CONC, 0.55);
      if (full < b.n && frac > 0.001)
        solid(
          box(
            b.x,
            y0 + full * b.uh,
            b.z,
            b.w * 1.01,
            b.uh * 0.94 * Math.max(0.25, frac),
            b.d * 1.01,
          ),
          ORANGE,
          0.6,
        );
      if (full < b.n) {
        S.ghosts.push({
          m: box(b.x, y0 + full * b.uh, b.z, b.w, (b.n - full) * b.uh, b.d),
          c: rgba(acc, 0.13),
        });
        for (let i = full; i < b.n; i++)
          S.gedges.push({ m: box(b.x, y0 + i * b.uh, b.z, b.w, b.uh, b.d), c: rgba(acc, 0.5) });
      }
    } else if (b.kind === 'seg') {
      const p = role(b.role);
      const done = p * b.n;
      const full = Math.floor(done + 1e-6);
      const frac = done - full;
      const col = hex(b.col || '#b7bcc4');
      const along = b.w;
      const sw = along / b.n;
      const y0 = blockBase(s, b);
      for (let i = 0; i < b.n; i++) {
        const lx = b.x - along / 2 + (i + 0.5) * sw;
        const m = box(lx, y0, b.z, sw * 0.98, b.h, b.d);
        if (i < full) solid(m, col);
        else if (i === full && frac > 0.001)
          solid(box(lx, y0, b.z, sw * 0.98, b.h * Math.max(0.25, frac), b.d), ORANGE, 0.6);
        else {
          S.ghosts.push({ m, c: rgba(acc, 0.14) });
          S.gedges.push({ m, c: rgba(acc, 0.45) });
        }
      }
    } else if (b.kind === 'road') {
      const p = role(b.role);
      const done = p * b.n;
      const full = Math.floor(done + 1e-6);
      const frac = done - full;
      const sw = (b.x1 - b.x0) / b.n;
      const y = s.FH - s.yRef + 0.04;
      for (let i = 0; i < b.n && i <= full; i++) {
        const cx = b.x0 + (i + 0.5) * sw;
        if (i < full) {
          solid(box(cx, y, b.z, sw * 0.995, 0.16, b.w), hex('#3f4650'), 0.25);
          for (const k of [-0.25, 0.25])
            S.solids.push({
              m: box(cx + sw * k, y + 0.16, b.z, sw * 0.3, 0.02, 0.18),
              c: [1, 1, 1, 1],
            });
        } else if (frac > 0.001)
          solid(box(b.x0 + i * sw + (sw * frac) / 2, y, b.z, sw * frac, 0.17, b.w), ORANGE, 0.5);
      }
    } else if (b.kind === 'water') {
      if (role(b.role) >= 0.95)
        S.trans.push({
          m: box(b.x, blockBase(s, b), b.z, b.w * 0.7, s.pit - 0.8, b.d * 0.7),
          c: [0.35, 0.6, 0.85, 0.6],
        });
    }
  }
  return S;
}

/** 重機の配置と搬出ルート（サンプルの computeEquip）。土工中だけ。 */
export interface Equip {
  ex: [number, number][];
  ro: [number, number][];
  route: [number, number, number][];
  cum: number[];
  trucks: number;
}

export function computeEquip(
  d: SiteDraw,
  st: SiteState,
  plan: { ex: number; rollers: number; trucks: number },
): Equip | null {
  const e = st.e;
  if (!(e > 0.002 && e < 0.998)) return null;
  const s = d.site;
  const g = s.g;
  const band = 0.12;
  const w = s.area.w;
  const fx = -w / 2 + clamp(e * (1 + band) - band * 0.5, 0, 1) * w;
  const pick = (sign: number, count: number): [number, number][] => {
    let cand: [number, number][] = [];
    for (let R = 6; R <= 120; R *= 1.6) {
      cand = [];
      for (let k = 0; k < g.nv; k++) {
        if (Math.abs(g.LX[k]! - fx) < R) {
          const v = (g.E[k]! - g.D[k]!) * sign;
          if (v > 0.4) cand.push([v, k]);
        }
      }
      if (cand.length >= count * 4) break;
    }
    cand.sort((a, b) => b[0] - a[0]);
    const out: number[] = [];
    for (const [, k] of cand) {
      if (out.every((o) => Math.hypot(g.LX[o]! - g.LX[k]!, g.LZ[o]! - g.LZ[k]!) > 14)) out.push(k);
      if (out.length >= count) break;
    }
    return out.map((k) => [g.LX[k]!, g.LZ[k]!]);
  };
  let ex = pick(1, clamp(plan.ex, 1, 3));
  const ro = pick(-1, clamp(plan.rollers, 0, 2));
  if (!ex.length) ex = ro.length ? [ro[0]!] : [[fx, 0]];
  const gt = s.gate;
  const gin: [number, number] = [gt.x - gt.ox * 4, gt.z - gt.oz * 4];
  const pts: [number, number, number][] = [];
  const [x0, z0] = ex[0]!;
  for (let k = 0; k <= 10; k++) {
    const lx = lerp(x0, gin[0], k / 10);
    const lz = lerp(z0, gin[1], k / 10);
    pts.push([lx, sampleH(d, lx, lz) + 0.1, lz]);
  }
  for (let k = 0; k <= 12; k++) {
    const lx = gt.x + gt.ox * k * 12;
    const lz = gt.z + gt.oz * k * 12;
    pts.push([lx, sampleH(d, lx, lz) + 0.15, lz]);
  }
  const cum = [0];
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1]!;
    const b = pts[i]!;
    cum.push(cum[i - 1]! + Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]));
  }
  return { ex, ro, route: pts, cum, trucks: clamp(plan.trucks, 0, 5) };
}

const YEL = hex('#eab308');
const DARK = hex('#3f3f46');
const SOILC = hex('#8a6a42');

export interface DynLayers {
  equip: boolean;
  route: boolean;
  crane: boolean;
}

/** 動くもの（タワークレーン・重機・ダンプ・搬出ルート）。t は秒。サンプルの dynItems。 */
export function dynamicItems(
  d: SiteDraw,
  st: SiteState,
  eq: Equip | null,
  L: DynLayers,
  t: number,
): { sol: Item[]; tr: Item[] } {
  const s = d.site;
  const sol: Item[] = [];
  const tr: Item[] = [];
  const fr = st.roles.frame ?? 0;
  const fo = st.roles.found ?? 0;
  if (s.crane && (fo > 0.3 || fr > 0) && fr < 1) {
    const cc = s.crane;
    const mx = cc.x;
    const mz = cc.z;
    const yb = sampleH(d, mx, mz);
    const h = cc.h;
    const col = rgba(YEL);
    sol.push(
      { m: trs(mx, yb, mz, 0, 2.4, h, 2.4), c: col },
      { m: trs(mx, yb + h, mz, 0, 3.4, 3, 3.4), c: [0.95, 0.96, 0.97, 1] },
    );
    const a = t * 0.12;
    const dx = Math.cos(a);
    const dz = -Math.sin(a);
    const off = (cc.jib - 12) / 2;
    sol.push(
      { m: trs(mx + dx * off, yb + h + 3, mz + dz * off, a, cc.jib + 12, 1.4, 1.4), c: col },
      { m: trs(mx - dx * 9, yb + h + 0.6, mz - dz * 9, a, 4, 2.6, 3), c: rgba(hex('#6b7280')) },
    );
    const r = cc.jib * (0.55 + 0.3 * Math.sin(t * 0.35));
    const hy = yb + h * 0.5 + h * 0.15 * Math.sin(t * 0.5);
    sol.push(
      {
        m: trs(mx + dx * r, hy, mz + dz * r, a, 0.25, yb + h + 3 - hy, 0.25),
        c: [0.2, 0.2, 0.2, 1],
      },
      { m: trs(mx + dx * r, hy - 1.6, mz + dz * r, a, 3, 1.6, 1.4), c: rgba(hex('#64748b')) },
    );
    if (L.crane) {
      for (const [R, al] of [
        [cc.jib, 0.55],
        [cc.jib + 5, 0.35],
      ] as const) {
        for (let k = 0; k < 48; k++) {
          const a0 = (k / 48) * Math.PI * 2;
          const a1 = ((k + 1) / 48) * Math.PI * 2;
          const ax = ((Math.cos(a0) + Math.cos(a1)) / 2) * R;
          const az = ((Math.sin(a0) + Math.sin(a1)) / 2) * R;
          tr.push({
            m: trs(
              mx + ax,
              sampleH(d, mx + ax, mz + az) + 0.4,
              mz + az,
              -(a0 + a1) / 2 + Math.PI / 2,
              2 * R * Math.sin(Math.PI / 48),
              0.3,
              R === cc.jib ? 0.8 : 0.5,
            ),
            c: [0.96, 0.6, 0.1, al],
          });
        }
      }
    }
  }
  const q = eq;
  if (!q) return { sol, tr };
  if (L.equip) {
    q.ex.forEach(([lx, lz], k) => {
      const y = sampleH(d, lx, lz);
      const a0 = k * 1.7 + 0.6;
      const a2 = a0 + 0.7 * Math.sin(t * 1.2 + k * 2);
      const fx = Math.cos(a2);
      const fz = -Math.sin(a2);
      const qx = Math.sin(a2);
      const qz = Math.cos(a2);
      sol.push(
        { m: trs(lx, y, lz, a0, 4.6, 1, 3.6), c: rgba(DARK) },
        { m: trs(lx, y + 1, lz, a2, 3.2, 1.6, 2.8), c: rgba(YEL) },
        {
          m: trs(lx - fx * 0.4 + qx * 0.7, y + 2.6, lz - fz * 0.4 + qz * 0.7, a2, 1.3, 1.4, 1.2),
          c: [0.85, 0.9, 0.95, 1],
        },
        { m: trs(lx + fx * 3.2, y + 3.2, lz + fz * 3.2, a2, 4.6, 0.55, 0.6), c: rgba(YEL) },
        { m: trs(lx + fx * 5.4, y - 0.2, lz + fz * 5.4, a2, 0.5, 3.6, 0.5), c: rgba(YEL) },
        { m: trs(lx + fx * 5.7, y - 0.6, lz + fz * 5.7, a2, 1.2, 0.9, 1.1), c: rgba(DARK) },
      );
    });
    q.ro.forEach(([lx, lz], k) => {
      const x = lx + 4 * Math.sin(t * 0.6 + k);
      const y = sampleH(d, x, lz);
      sol.push(
        { m: trs(x, y + 0.4, lz, 0, 3.2, 1.8, 2), c: rgba(YEL) },
        { m: trs(x + 2.2, y, lz, 0, 1.4, 1.4, 2.2), c: rgba(hex('#71717a')) },
      );
    });
    const R = q.route;
    const cum = q.cum;
    const Lt = Math.max(1, cum[cum.length - 1]!);
    for (let k = 0; k < q.trucks; k++) {
      const u = ((t * 12) / Lt + k / q.trucks) % 1;
      const sd = u * 2 * Lt;
      const fwd = sd < Lt;
      const dd = fwd ? sd : 2 * Lt - sd;
      let i = 1;
      while (i < cum.length - 1 && cum[i]! < dd) i++;
      const f = (dd - cum[i - 1]!) / Math.max(1e-6, cum[i]! - cum[i - 1]!);
      const p0 = R[i - 1]!;
      const p1 = R[i]!;
      const x = lerp(p0[0], p1[0], f);
      const y = lerp(p0[1], p1[1], f);
      const z = lerp(p0[2], p1[2], f);
      let dx = p1[0] - p0[0];
      let dz = p1[2] - p0[2];
      if (!fwd) {
        dx = -dx;
        dz = -dz;
      }
      const a = Math.atan2(-dz, dx);
      const hx = Math.cos(a);
      const hz = -Math.sin(a);
      sol.push(
        { m: trs(x, y + 0.3, z, a, 6.6, 0.6, 2.2), c: rgba(DARK) },
        { m: trs(x + hx * 2.3, y + 0.8, z + hz * 2.3, a, 1.9, 2.1, 2.3), c: [0.95, 0.96, 0.97, 1] },
        { m: trs(x - hx * 0.9, y + 0.9, z - hz * 0.9, a, 4.4, 1.5, 2.4), c: rgba(hex('#d97706')) },
      );
      if (fwd)
        sol.push({ m: trs(x - hx * 0.9, y + 2.4, z - hz * 0.9, a, 3.8, 0.6, 2), c: rgba(SOILC) });
    }
  }
  if (L.route) {
    const R = q.route;
    for (let i = 1; i < R.length; i++) {
      const p0 = R[i - 1]!;
      const p1 = R[i]!;
      const dx = p1[0] - p0[0];
      const dz = p1[2] - p0[2];
      if (Math.hypot(dx, dz) < 0.05) continue;
      tr.push({
        m: trs(
          (p0[0] + p1[0]) / 2,
          (p0[1] + p1[1]) / 2 + 0.15,
          (p0[2] + p1[2]) / 2,
          Math.atan2(-dz, dx),
          Math.hypot(dx, dz),
          0.12,
          1.4,
        ),
        c: [0.96, 0.62, 0.04, 0.8],
      });
    }
  }
  return { sol, tr };
}
